import axios from "axios";
import { RUNTIME_API_BASE, RUNTIME_PUBLIC_BASE } from "./runtimeBase";

const API_BASE_KEY = "nidcs_api_base_v1";
const API_PATH_CANDIDATES = Array.from(
  new Set([RUNTIME_API_BASE || "/api", "/api", "/backend/public/api", "/finaloutput/public_html/api", "/finaloutput/laravel/public/api", "/laravel/public/api"])
);
const LEGACY_API_PREFIXES = ["/backend/public/api", "/finaloutput/laravel/public/api", "/laravel/public/api"];
const LEGACY_PUBLIC_PREFIXES = ["/backend/public"];

function normalizeBase(base) {
  return String(base || "").replace(/\/+$/, "");
}

function getStoredBase() {
  try {
    const stored = localStorage.getItem(API_BASE_KEY);
    if (stored && API_PATH_CANDIDATES.includes(stored)) return stored;
  } catch (_error) {
    // Ignore storage errors.
  }
  return null;
}

function setStoredBase(base) {
  try {
    localStorage.setItem(API_BASE_KEY, base);
  } catch (_error) {
    // Ignore storage errors.
  }
}

export function installApiBaseResolver() {
  const stored = getStoredBase();
  const orderedBases = stored
    ? [stored, ...API_PATH_CANDIDATES.filter((item) => item !== stored)]
    : [...API_PATH_CANDIDATES];

  axios.interceptors.request.use((config) => {
    if (!config || typeof config.url !== "string") return config;
    if (/^https?:\/\//i.test(config.url)) return config;

    // Normalize legacy hardcoded public base paths to current runtime base.
    for (const prefix of LEGACY_PUBLIC_PREFIXES) {
      if (config.url === prefix || config.url.startsWith(`${prefix}/`)) {
        const suffix = config.url.slice(prefix.length);
        config.url = `${RUNTIME_PUBLIC_BASE}${suffix}` || suffix || "/";
      }
    }

    // Normalize legacy absolute API prefixes first.
    for (const prefix of LEGACY_API_PREFIXES) {
      if (config.url === prefix || config.url.startsWith(`${prefix}/`)) {
        const suffix = config.url.slice(prefix.length);
        config.url = `${RUNTIME_API_BASE}${suffix}`;
      }
    }

    if (!config.url.startsWith("/api/")) {
      if (!(RUNTIME_API_BASE && config.url.startsWith(`${RUNTIME_API_BASE}/`))) {
        return config;
      }
      // Convert runtime-specific API URL back to canonical /api/* flow for retry logic.
      config.url = `/api${config.url.slice(RUNTIME_API_BASE.length)}`;
    }

    const base = config._apiBase || orderedBases[0];
    const suffix = config.url.slice("/api".length);
    config._apiOriginalUrl = config.url;
    config._apiRetryIndex = typeof config._apiRetryIndex === "number" ? config._apiRetryIndex : 0;
    config._apiBase = base;
    config.url = `${normalizeBase(base)}${suffix}`;
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const base = response?.config?._apiBase;
      if (base && API_PATH_CANDIDATES.includes(base)) {
        setStoredBase(base);
      }
      return response;
    },
    async (error) => {
      const config = error?.config;
      if (!config || !config._apiOriginalUrl || typeof config._apiRetryIndex !== "number") {
        return Promise.reject(error);
      }

      const status = error?.response?.status ?? 0;
      const retriableStatus = status === 0 || status === 404 || status >= 500;
      if (!retriableStatus) {
        return Promise.reject(error);
      }

      const nextIndex = config._apiRetryIndex + 1;
      if (nextIndex >= orderedBases.length) {
        return Promise.reject(error);
      }

      config._apiRetryIndex = nextIndex;
      config._apiBase = orderedBases[nextIndex];
      config.url = config._apiOriginalUrl;
      return axios(config);
    }
  );
}
